import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, AlertTriangle, AlertCircle, HelpCircle, Check, Pencil, X, Sparkles, RefreshCw,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  adminStyleCheck, adminStyleVerify, adminStyleDismiss, adminStyleSuggest,
} from '../services/api';

const BAND = {
  green:  { label: 'Rendben',   text: 'text-emerald-300', dot: 'bg-emerald-400' },
  yellow: { label: 'Javítandó', text: 'text-yellow-300',  dot: 'bg-yellow-400' },
  red:    { label: 'Átírandó',  text: 'text-red-300',     dot: 'bg-red-500' },
};

const RULE_LABEL = {
  duplicate_sentence: 'Ismétlődő mondat',
  repeated_phrase: 'Ismétlődő fordulat',
  same_structure: 'Egyforma szerkezet',
  em_dash: 'Gondolatjel-sűrűség',
  bold: 'Félkövér sűrűség',
  emoji: 'Emoji',
  not_x_but_y: '„Nem X, hanem Y"',
  boilerplate: 'Sablonos felvezetés',
  hype: 'Túlzó jelző',
  experience: 'Tapasztalati állítás',
  unsourced_number: 'Forrás nélküli szám',
  unused_source: 'Felhasználatlan forrás',
  person_mix: 'Személy-következetlenség',
  outdated_name: 'Elavult név',
};

const SEV = {
  red:    { label: 'piros', cls: 'border-red-500/40 bg-red-500/5',        badge: 'bg-red-500/20 text-red-200',        Icon: AlertCircle },
  yellow: { label: 'sárga', cls: 'border-yellow-500/40 bg-yellow-500/5',  badge: 'bg-yellow-500/20 text-yellow-200',  Icon: AlertTriangle },
  info:   { label: 'kérdés', cls: 'border-sky-500/40 bg-sky-500/5',       badge: 'bg-sky-500/20 text-sky-200',        Icon: HelpCircle },
};

export const StyleScoreBadge = ({ score, band, red = 0, yellow = 0, testId }) => {
  const meta = BAND[band] || BAND.green;
  return (
    <span data-testid={testId} className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest">
      <span className={`inline-flex items-center gap-1.5 ${meta.text} bg-white/5 px-2 py-0.5 rounded`}>
        <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
        {meta.label} · {score}
      </span>
      {(red > 0 || yellow > 0) && (
        <span className="text-gray-500 normal-case tracking-normal font-semibold">
          Javítandó: <span className="text-red-300">{red} piros</span>,{' '}
          <span className="text-yellow-300">{yellow} sárga</span>
        </span>
      )}
    </span>
  );
};

const FindingCard = ({ adminKey, slug, title, finding, onApply, onResolved }) => {
  const sev = SEV[finding.severity] || SEV.yellow;
  const [suggestion, setSuggestion] = useState(finding.suggestion || '');
  const [loading, setLoading] = useState(false);

  const askAi = async () => {
    setLoading(true);
    try {
      const { suggestion: s } = await adminStyleSuggest(adminKey, {
        quote: finding.quote, rule: finding.rule, title,
      });
      setSuggestion(s);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A javaslat nem készült el.');
    } finally {
      setLoading(false);
    }
  };

  const resolve = async (action) => {
    try {
      if (action === 'verify') await adminStyleVerify(adminKey, slug, finding.id, finding.quote);
      else await adminStyleDismiss(adminKey, slug, finding.id, finding.quote);
      onResolved(finding.id);
    } catch {
      toast.error('A művelet nem sikerült.');
    }
  };

  return (
    <div
      data-testid={`style-finding-${finding.id}`}
      className={`rounded-lg border p-3 space-y-2 ${sev.cls}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-300 flex items-center gap-1.5">
          <sev.Icon size={12} /> {RULE_LABEL[finding.rule] || finding.rule}
        </span>
        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${sev.badge}`}>{sev.label}</span>
      </div>

      <button
        type="button"
        onClick={() => onApply({ mode: 'locate', finding })}
        className="block w-full text-left text-xs text-gray-200 bg-black/30 rounded px-2 py-1.5 hover:bg-black/50 transition-colors"
      >
        „{finding.quote}"
      </button>

      <p className="text-[11px] text-gray-400 leading-relaxed">{finding.why}</p>
      {finding.others?.length > 0 && (
        <p className="text-[11px] text-gray-500">
          Másik cikk:{' '}
          <a
            href={`/tudastar/${finding.others[0].slug}`}
            target="_blank"
            rel="noreferrer"
            className="text-yellow-300 hover:underline"
          >
            {finding.others[0].title || finding.others[0].slug}
          </a>
        </p>
      )}
      {finding.tip && <p className="text-[11px] text-emerald-300/90">Tipp: {finding.tip}</p>}

      {suggestion && (
        <div className="rounded bg-emerald-500/10 border border-emerald-600/30 px-2 py-1.5 text-xs text-emerald-100">
          {suggestion}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 pt-1">
        {finding.needs_confirmation ? (
          <Button
            size="sm"
            data-testid={`style-verify-${finding.id}`}
            onClick={() => resolve('verify')}
            className="h-7 bg-sky-600 hover:bg-sky-700 text-white text-[11px] font-bold"
          >
            <Check size={12} className="mr-1" /> Ellenőrizve
          </Button>
        ) : (
          <>
            {suggestion && (
              <>
                <Button
                  size="sm"
                  data-testid={`style-accept-${finding.id}`}
                  onClick={() => onApply({ mode: 'accept', finding, suggestion })}
                  className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold"
                >
                  <Check size={12} className="mr-1" /> Elfogadom
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`style-edit-${finding.id}`}
                  onClick={() => onApply({ mode: 'edit', finding, suggestion })}
                  className="h-7 text-[11px]"
                >
                  <Pencil size={12} className="mr-1" /> Szerkesztem
                </Button>
              </>
            )}
            {!finding.is_density && (
              <Button
                size="sm"
                variant="outline"
                data-testid={`style-ask-${finding.id}`}
                onClick={askAi}
                disabled={loading}
                className="h-7 text-[11px] border-yellow-600/40 text-yellow-200"
              >
                {loading
                  ? <><Loader2 size={12} className="mr-1 animate-spin" /> Kérem…</>
                  : <><Sparkles size={12} className="mr-1" /> Javaslatot kérek</>}
              </Button>
            )}
          </>
        )}
        <Button
          size="sm"
          variant="ghost"
          data-testid={`style-dismiss-${finding.id}`}
          onClick={() => resolve('dismiss')}
          className="h-7 text-[11px] text-gray-400"
        >
          <X size={12} className="mr-1" /> Elvetem
        </Button>
      </div>
    </div>
  );
};

export const StyleCheckPanel = ({ adminKey, slug, title, onApply }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      setReport(await adminStyleCheck(adminKey, slug));
    } catch {
      toast.error('A stílusellenőrzés nem futott le.');
    } finally {
      setLoading(false);
    }
  }, [adminKey, slug]);

  useEffect(() => { load(); }, [load]);

  const resolved = (id) =>
    setReport((r) => ({ ...r, findings: r.findings.filter((f) => f.id !== id) }));

  if (!slug) return null;
  const grouped = (report?.findings || []).reduce((acc, f) => {
    const key = ['duplicate_sentence', 'repeated_phrase', 'same_structure'].includes(f.rule)
      ? 'Cikkek közötti ismétlés'
      : ['experience', 'unsourced_number', 'unused_source', 'person_mix', 'outdated_name'].includes(f.rule)
        ? 'Hitelesség'
        : 'Stílus és sűrűség';
    acc[key] = acc[key] || [];
    acc[key].push(f);
    return acc;
  }, {});

  return (
    <aside data-testid="style-check-panel" className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
          Stílusellenőrző
        </div>
        <button
          type="button"
          data-testid="style-check-refresh"
          onClick={load}
          className="text-gray-500 hover:text-white"
          title="Újraszámolás"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {loading && !report ? (
        <div className="text-xs text-gray-500 flex items-center gap-2">
          <Loader2 size={13} className="animate-spin" /> Ellenőrzés…
        </div>
      ) : report ? (
        <>
          <StyleScoreBadge
            score={report.style_score}
            band={report.band}
            red={report.red}
            yellow={report.yellow}
            testId="style-check-score"
          />
          {report.breakdown?.length > 0 && (
            <div className="text-[11px] text-gray-400 space-y-0.5">
              {report.breakdown.map((b) => (
                <div key={b.rule} className="flex justify-between gap-2">
                  <span className="truncate">{RULE_LABEL[b.rule] || b.rule}</span>
                  <span className="text-red-300 font-mono">-{b.points}</span>
                </div>
              ))}
            </div>
          )}
          {report.findings.length === 0 ? (
            <p className="text-xs text-emerald-300">Nincs jelzés — ez a cikk rendben van.</p>
          ) : (
            Object.entries(grouped).map(([group, items]) => (
              <div key={group} className="space-y-2">
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-600 pt-1">
                  {group} ({items.length})
                </div>
                {items.map((f) => (
                  <FindingCard
                    key={f.id}
                    adminKey={adminKey}
                    slug={slug}
                    title={title}
                    finding={f}
                    onApply={onApply}
                    onResolved={resolved}
                  />
                ))}
              </div>
            ))
          )}
        </>
      ) : null}
    </aside>
  );
};

export const useStyleSummaries = (adminKey) => {
  const [rows, setRows] = useState({});
  const load = useCallback(async () => {
    try {
      const { posts } = await adminStyleCheck(adminKey);
      setRows(Object.fromEntries(posts.map((p) => [p.slug, p])));
    } catch { /* néma: a lista pontszám nélkül is működik */ }
  }, [adminKey]);
  useEffect(() => { if (adminKey) load(); }, [adminKey, load]);
  return { rows, reload: load };
};
